import { defineStore } from 'pinia'
import { ref } from 'vue'

// Types
interface Notification {
  id: string
  message: string
  type: 'success' | 'danger' | 'warning' | 'info'
  duration?: number
}

export const useAppStore = defineStore('app', () => {
  // State
  const notifications = ref<Notification[]>([])
  const isAutosaving = ref(false)
  const lastSaveTime = ref<Date | null>(null)

  // Notification management
  const addNotification = (
    message: string,
    type: Notification['type'] = 'info',
    duration = 5000
  ) => {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    const notification: Notification = {
      id,
      message,
      type,
      duration,
    }

    notifications.value.push(notification)

    // Auto-remove notification after duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id)
      }, duration)
    }

    return id
  }

  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  const clearAllNotifications = () => {
    notifications.value = []
  }

  // Autosave management
  const setAutosaving = (saving: boolean) => {
    isAutosaving.value = saving
    if (!saving) {
      lastSaveTime.value = new Date()
    }
  }

  // Utility functions for common notification types
  const showSuccess = (message: string, duration?: number) => {
    return addNotification(message, 'success', duration)
  }

  const showError = (message: string, duration?: number) => {
    return addNotification(message, 'danger', duration)
  }

  const showWarning = (message: string, duration?: number) => {
    return addNotification(message, 'warning', duration)
  }

  const showInfo = (message: string, duration?: number) => {
    return addNotification(message, 'info', duration)
  }

  return {
    // State
    notifications,
    isAutosaving,
    lastSaveTime,

    // Notification actions
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,

    // Autosave actions
    setAutosaving,
  }
})
